import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, UserMinus, UserX, AlertTriangle, FileCheck2, Repeat, Wallet } from 'lucide-react';
import Layout from '../components/Layout';
import CompanyFilter from '../components/CompanyFilter';
import { api, formatCurrency } from '../lib/api';

// Card with colored top bar (defined outside parent to avoid no-unstable-nested-components)
const KPI = ({ label, value, sublabel, color, icon: Icon, accent, valueClass = '', testId }) => (
  <div data-testid={testId} className="bg-surface border border-border relative overflow-hidden stat-card fade-up">
    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
    <div className="p-5 pt-6">
      <div className="flex justify-between items-start mb-3">
        <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary font-semibold">{label}</p>
        {Icon && <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.5} />}
      </div>
      <p className={`font-numbers text-3xl text-text-primary tabular-nums font-medium ${valueClass}`}>{value}</p>
      {sublabel && <p className="font-body text-xs text-text-secondary mt-1">{sublabel}</p>}
      {accent && <div className="mt-3 pt-3 border-t border-border"><p className="font-body text-[10px] uppercase tracking-widest text-text-secondary">{accent}</p></div>}
    </div>
  </div>
);

const BillingHealth = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
  }, []);

  useEffect(() => {
    const params = selectedCompany ? { company_id: selectedCompany } : {};
    api.get('/dashboard/billing-health', { params }).then(r => setData(r.data));
  }, [selectedCompany]);

  if (!data) {
    return <Layout title="Billing Health"><div className="text-center py-12 font-body text-text-secondary">Loading...</div></Layout>;
  }

  const cc = data.client_counts;
  const ic = data.invoice_counts;
  const mrr = data.mrr;
  const gst = data.gst_summary;

  return (
    <Layout title="Billing Health" action={<CompanyFilter companies={companies} selected={selectedCompany} onChange={setSelectedCompany} />}>
      {/* CLIENT STATUS ROW */}
      <div className="mb-2">
        <p className="font-body text-xs uppercase tracking-[0.25em] text-text-secondary mb-3 font-semibold">— Client Overview</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI testId="kpi-total-clients" label="Total Clients" value={cc.total} sublabel="All clients across companies" color="#1E2A4A" icon={Users} accent="ACROSS ALL ENTITIES" />
        <KPI testId="kpi-active-clients" label="Active" value={cc.active} sublabel="Currently billable" color="#2E5BFF" icon={UserCheck} />
        <KPI testId="kpi-on-hold-clients" label="On Hold" value={cc.on_hold} sublabel="Paused retainers" color="#B8A96A" icon={UserMinus} />
        <KPI testId="kpi-inactive-clients" label="Inactive" value={cc.inactive} sublabel="Not currently billed" color="#5C1A2A" icon={UserX} />
      </div>

      {/* BILLING SUMMARY ROW */}
      <div className="mb-2">
        <p className="font-body text-xs uppercase tracking-[0.25em] text-text-secondary mb-3 font-semibold">— This Month</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <KPI testId="kpi-overdue-invoices" label="Overdue Invoices" value={ic.overdue} sublabel={formatCurrency(ic.overdue_amount || 0)} color="#5C1A2A" icon={AlertTriangle} valueClass="text-maroon" />
        <KPI testId="kpi-pending-invoices" label="Pending Invoices" value={ic.pending || 0} sublabel={formatCurrency(ic.pending_amount || 0)} color="#B8A96A" icon={FileCheck2} />
        <KPI testId="kpi-invoiced" label="Invoiced ✓" value={ic.invoiced_this_month} sublabel="Sent this month" color="#2A4A35" icon={FileCheck2} valueClass="text-success" />
        <KPI testId="kpi-mrr" label="Monthly Retainer (MRR)" value={formatCurrency(mrr.base)} sublabel="Base recurring revenue" color="#B8A96A" icon={Repeat} />
        <KPI testId="kpi-total-invoiceable" label="Total Invoiceable" value={formatCurrency(mrr.total_invoiceable)} sublabel="Including GST" color="#2E5BFF" icon={Wallet} />
        <KPI testId="kpi-outstanding" label="Outstanding Total" value={formatCurrency((ic.overdue_amount || 0) + (ic.pending_amount || 0))} sublabel="Pending + Overdue" color="#7B1B1B" icon={AlertTriangle} valueClass="text-maroon" />
      </div>

      {/* GST BILLING SUMMARY */}
      <div data-testid="gst-summary" className="bg-surface border border-border p-6 fade-up fade-up-d1">
        <div className="flex justify-between items-baseline mb-1">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-text-secondary font-semibold">— Tax Breakdown</p>
        </div>
        <h2 className="font-heading text-xl font-bold text-text-primary mb-6">GST Billing Summary</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-ink text-white">
                <th className="font-body text-[10px] uppercase tracking-[0.2em] text-left px-4 py-3 font-semibold">Category</th>
                <th className="font-body text-[10px] uppercase tracking-[0.2em] text-right px-4 py-3 font-semibold">Clients</th>
                <th className="font-body text-[10px] uppercase tracking-[0.2em] text-right px-4 py-3 font-semibold">Base Retainer</th>
                <th className="font-body text-[10px] uppercase tracking-[0.2em] text-right px-4 py-3 font-semibold">GST 18%</th>
                <th className="font-body text-[10px] uppercase tracking-[0.2em] text-right px-4 py-3 font-semibold">Total Invoice</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="row-gst-clients" className="border-b border-border hover:bg-cream transition-colors">
                <td className="px-4 py-4">
                  <p className="font-body text-sm font-medium text-text-primary">GST Clients</p>
                  <p className="font-body text-xs text-text-secondary">Clients with valid GSTIN</p>
                </td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-medium">{gst.gst_clients.count}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums">{formatCurrency(gst.gst_clients.base_retainer)}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums text-gold font-medium">{formatCurrency(gst.gst_clients.gst)}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-semibold">{formatCurrency(gst.gst_clients.total)}</td>
              </tr>
              <tr data-testid="row-non-gst-clients" className="border-b border-border hover:bg-cream transition-colors">
                <td className="px-4 py-4">
                  <p className="font-body text-sm font-medium text-text-primary">Non-GST Clients</p>
                  <p className="font-body text-xs text-text-secondary">No GST applicable</p>
                </td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-medium">{gst.non_gst_clients.count}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums">{formatCurrency(gst.non_gst_clients.base_retainer)}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums text-text-secondary">—</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-semibold">{formatCurrency(gst.non_gst_clients.total)}</td>
              </tr>
              <tr data-testid="row-total" className="bg-cream">
                <td className="px-4 py-4">
                  <p className="font-body text-sm font-bold text-text-primary uppercase tracking-wider">Total</p>
                </td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-bold">{gst.total.count}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-bold">{formatCurrency(gst.total.base_retainer)}</td>
                <td className="px-4 py-4 text-right font-numbers text-base tabular-nums font-bold text-gold">{formatCurrency(gst.total.gst)}</td>
                <td className="px-4 py-4 text-right font-numbers text-lg tabular-nums font-bold text-maroon">{formatCurrency(gst.total.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary mb-1">Effective GST Rate</p>
            <p className="font-numbers text-xl tabular-nums font-medium">{gst.total.base_retainer > 0 ? ((gst.total.gst / gst.total.base_retainer) * 100).toFixed(2) : '0.00'}%</p>
          </div>
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary mb-1">GST Client Coverage</p>
            <p className="font-numbers text-xl tabular-nums font-medium">{gst.total.count > 0 ? Math.round((gst.gst_clients.count / gst.total.count) * 100) : 0}%</p>
          </div>
          <div>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary mb-1">Annual Run Rate</p>
            <p className="font-numbers text-xl tabular-nums font-medium text-success">{formatCurrency(gst.total.total * 12)}</p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
          <p className="font-body text-xs text-text-secondary">
            <span className="text-maroon font-medium">Note:</span> MRR is calculated from active monthly recurring templates. Pause templates from the <button onClick={() => navigate('/recurring')} className="text-maroon underline hover:text-maroon-hover">Recurring page</button>.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default BillingHealth;
